import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  TextInput,
  Platform,
  Alert,
  Linking,
  useWindowDimensions,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import axios from "axios";
import { useAuth } from "../../AuthContext";
import { FASTAPI_BASE_URL, PDF_BASE_URL } from "../../config";
import TestStatsRow from "./TestStatsRow";
import { useTranslation } from "../../i18n/useTranslation";
import { gradeQuizAnswer, indexSet } from "../lessonPlans/lessonQuizGrading";

// ---------------------------------------------------------------------------
// Question-type labels
// ---------------------------------------------------------------------------
const QTYPE_LABELS = {
  1: "Fill in the Blank",
  2: "MCQ",
  4: "True / False",
  9: "Word Pairs",
  10: "Short Essay",
  11: "Long Essay",
};
const qtypeLabel = (qt, t) => {
  const label = QTYPE_LABELS[Number(qt)];
  return label ? t(label) : t("Type {type}", { type: qt });
};

// ---------------------------------------------------------------------------
// Avatar colour palette (deterministic from name hash)
// ---------------------------------------------------------------------------
const AVATAR_COLORS = [
  "#6366f1", "#0d9488", "#d97706", "#dc2626", "#16a34a",
  "#7c3aed", "#0ea5e9", "#e11d48", "#84cc16", "#f97316",
];
function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
function avatarColor(name) {
  return AVATAR_COLORS[hashStr(name) % AVATAR_COLORS.length];
}
function initials(name) {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name.slice(0, 2) || "??").toUpperCase();
}

// ---------------------------------------------------------------------------
// Grade distribution bucket config
// ---------------------------------------------------------------------------
const BUCKETS = [
  { min: 0, max: 39, label: "0-39" },
  { min: 40, max: 49, label: "40-49" },
  { min: 50, max: 59, label: "50-59" },
  { min: 60, max: 69, label: "60-69" },
  { min: 70, max: 79, label: "70-79" },
  { min: 80, max: 89, label: "80-89" },
  { min: 90, max: 100, label: "90-100" },
];
function bucketColor(idx, isDarkMode) {
  const pal = [
    "#ef4444", "#d97706", "#d97706", "#0d9488",
    "#0d9488", "#6366f1", "#16a34a",
  ];
  return pal[idx] || "#6366f1";
}
const BUCKET_OPACITY = [0.7, 0.7, 0.5, 0.6, 0.8, 0.8, 0.8];

// ---------------------------------------------------------------------------
// Helpers ported from TeacherTestReport.js
// ---------------------------------------------------------------------------
function parseJSONSafe(raw, fallback = null) {
  try { return raw ? JSON.parse(raw) : fallback; } catch { return fallback; }
}

function getMaxScore(entry) {
  const m = entry?.max_score;
  if (m == null) return 1;
  const n = Number(m);
  return Number.isFinite(n) ? n : 1;
}

function optionToText(optObj) {
  if (!optObj) return "";
  if (Array.isArray(optObj.segments)) {
    const text = optObj.segments
      .filter((seg) => seg && seg.type === "Text" && typeof seg.text === "string")
      .map((seg) => seg.text)
      .join(" ")
      .trim();
    if (text) return text;
  }
  return optObj.text || "";
}

export function autoSuggestScore(entry) {
  const maxScore = getMaxScore(entry);
  const parsedOptions = parseJSONSafe(entry.options, null);
  const rawStudent = parseJSONSafe(entry.student_answer, entry.student_answer ?? null);
  const qtype = entry.question_type;

  let correct = null;
  if (["1", "2", "3", "4"].includes(String(qtype))) {
    correct = parsedOptions?.correct_answer ?? null;
  }

  // FITB
  if (String(qtype) === "1") {
    if (rawStudent == null || correct == null) return { suggested: undefined, isAutoGradable: true };
    const ok = String(rawStudent).trim().toLowerCase() === String(correct).trim().toLowerCase();
    return { suggested: ok ? maxScore : 0, isAutoGradable: true };
  }
  // MCQ
  if (String(qtype) === "2") {
    let stuNum = null;
    if (typeof rawStudent === "string" && /^option\d+$/i.test(rawStudent))
      stuNum = parseInt(rawStudent.replace(/^\D+/g, ""), 10);
    else if (typeof rawStudent === "string" && /^\d+$/.test(rawStudent))
      stuNum = parseInt(rawStudent, 10);
    let corrNum = null;
    if (typeof correct === "string" && /^\d+$/.test(correct))
      corrNum = parseInt(correct, 10);
    else if (typeof correct === "string" && /^option\d+$/i.test(correct))
      corrNum = parseInt(correct.replace(/^\D+/g, ""), 10);
    if (Number.isFinite(stuNum) && Number.isFinite(corrNum))
      return { suggested: stuNum === corrNum ? maxScore : 0, isAutoGradable: true };
    return { suggested: undefined, isAutoGradable: true };
  }
  // True / False (type 3)
  if (String(qtype) === "3") {
    if (rawStudent == null || correct == null) return { suggested: undefined, isAutoGradable: true };
    const verdict = gradeQuizAnswer({ question_type: 3, options: parsedOptions }, rawStudent);
    return { suggested: verdict == null ? undefined : verdict ? maxScore : 0, isAutoGradable: true };
  }
  // Multi-select (type 4): full points only on an exact set match.
  if (String(qtype) === "4") {
    if (rawStudent == null || correct == null) return { suggested: undefined, isAutoGradable: true };
    if (indexSet(correct).size === 0) return { suggested: undefined, isAutoGradable: true };
    const verdict = gradeQuizAnswer({ question_type: 4, options: parsedOptions }, rawStudent);
    return { suggested: verdict === true ? maxScore : 0, isAutoGradable: true };
  }
  return { suggested: undefined, isAutoGradable: false };
}

// Selected/correct option texts of a multi-select question, comma-separated.
function multiSelectTexts(raw, parsedOptions) {
  return [...indexSet(raw)]
    .sort((a, b) => a - b)
    .map((n) => optionToText(parsedOptions?.["option" + n]) || "option" + n)
    .join(", ");
}

export function buildAnswerDisplay(entry, t) {
  const parsedOptions = parseJSONSafe(entry.options, null);
  const rawStudent = parseJSONSafe(entry.student_answer, entry.student_answer ?? null);

  let studentDisplay = t("No answer");
  if (rawStudent == null) {
    studentDisplay = t("No answer");
  } else if (String(entry.question_type) === "4") {
    studentDisplay = multiSelectTexts(rawStudent, parsedOptions) || t("No answer");
  } else if (typeof rawStudent === "string" && /^option\d+$/i.test(rawStudent) && parsedOptions) {
    const key = rawStudent;
    const obj = parsedOptions[key];
    const txt = optionToText(obj) || key;
    const m = key.match(/^option(\d+)$/i);
    studentDisplay = m?.[1] ? t("(Option {n}) {text}", { n: m[1], text: txt }) : txt;
  } else if (Array.isArray(rawStudent)) {
    studentDisplay = rawStudent.join(", ");
  } else {
    studentDisplay = String(rawStudent);
  }

  let correctDisplay = t("[N/A]");
  const qtype = String(entry.question_type);
  if (qtype === "4") {
    const corr = parsedOptions?.correct_answer;
    if (corr != null) correctDisplay = multiSelectTexts(corr, parsedOptions) || String(corr);
  } else if (["2", "3"].includes(qtype)) {
    const corr = parsedOptions?.correct_answer;
    if (corr != null) {
      if (typeof corr === "string" && /^\d+$/.test(corr) && parsedOptions) {
        const corrKey = "option" + corr;
        const obj = parsedOptions[corrKey];
        const txt = optionToText(obj) || corrKey;
        correctDisplay = t("(Option {n}) {text}", { n: corr, text: txt });
      } else if (typeof corr === "string" && /^option\d+$/i.test(corr) && parsedOptions) {
        const obj = parsedOptions[corr];
        const txt = optionToText(obj) || corr;
        const m = corr.match(/^option(\d+)$/i);
        correctDisplay = m?.[1] ? t("(Option {n}) {text}", { n: m[1], text: txt }) : txt;
      } else {
        correctDisplay = String(corr);
      }
    }
  } else if (qtype === "1") {
    const corr = parsedOptions?.correct_answer;
    if (corr != null) correctDisplay = String(corr);
  } else if (["10", "11"].includes(qtype)) {
    correctDisplay = t("[Teacher graded]");
  } else if (qtype === "9") {
    correctDisplay = t("[See pairs]");
  }

  return { studentDisplay, correctDisplay };
}

function fmtDate(d) {
  if (!d) return "N/A";
  try {
    return new Date(d).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch { return String(d); }
}

function openPDF(pdfPath) {
  const url = `${PDF_BASE_URL}/${pdfPath}`;
  if (Platform.OS === "web") {
    window.open(url, "_blank");
  } else {
    Linking.openURL(url);
  }
}

// ---------------------------------------------------------------------------
// Colors helper (mirrors TeacherTestsPage getColors)
// ---------------------------------------------------------------------------
function getColors(isDarkMode) {
  if (isDarkMode) {
    return {
      bg: "#0f172a",
      cardBg: "#1e293b",
      textPrimary: "#f1f5f9",
      textSecondary: "#94a3b8",
      textMuted: "#64748b",
      border: "#334155",
      accent: "#818cf8",
      accentHover: "#6366f1",
      accentLight: "rgba(99,102,241,0.12)",
      green: "#4ade80",
      greenBg: "rgba(22,163,74,0.12)",
      greenText: "#86efac",
      amber: "#fbbf24",
      amberBg: "rgba(217,119,6,0.12)",
      amberText: "#fcd34d",
      red: "#f87171",
      redBg: "rgba(239,68,68,0.12)",
      redText: "#fca5a5",
      teal: "#2dd4bf",
      tealBg: "rgba(13,148,136,0.12)",
      inputBg: "#0f172a",
      inputBorder: "#334155",
    };
  }
  return {
    bg: "#f8fafc",
    cardBg: "#ffffff",
    textPrimary: "#0f172a",
    textSecondary: "#475569",
    textMuted: "#94a3b8",
    border: "#e2e8f0",
    accent: "#6366f1",
    accentHover: "#4f46e5",
    accentLight: "#eef2ff",
    green: "#16a34a",
    greenBg: "#f0fdf4",
    greenText: "#166534",
    amber: "#d97706",
    amberBg: "#fef3c7",
    amberText: "#92400e",
    red: "#dc2626",
    redBg: "#fef2f2",
    redText: "#991b1b",
    teal: "#0d9488",
    tealBg: "#ccfbf1",
    inputBg: "#ffffff",
    inputBorder: "#e2e8f0",
  };
}

// ---------------------------------------------------------------------------
// Score badge colour
// ---------------------------------------------------------------------------
function scoreBadgeColors(pct, colors) {
  if (pct >= 80) return { bg: colors.greenBg, text: colors.greenText };
  if (pct >= 60) return { bg: colors.accentLight, text: colors.accent };
  if (pct >= 40) return { bg: colors.amberBg, text: colors.amberText };
  return { bg: colors.redBg, text: colors.redText };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function ResultsGradingTab({ isDarkMode, initialTestId }) {
  const { width: winWidth } = useWindowDimensions();
  const isMobile = winWidth < 768;
  const colors = getColors(isDarkMode);
  const { t } = useTranslation();

  const auth = useAuth();
  const user_id = auth?.user?.email || "unknown_teacher";

  // Test selector
  const [tests, setTests] = useState([]);
  const [selectedTestId, setSelectedTestId] = useState(initialTestId || null);
  const [loadingTests, setLoadingTests] = useState(false);

  // Results for chosen test
  const [results, setResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);

  // Student / grading state
  const [selectedStudentEmail, setSelectedStudentEmail] = useState(null);
  const [selectedAttemptId, setSelectedAttemptId] = useState(null);
  const [currentAttemptId, setCurrentAttemptId] = useState(null);
  const [gradingEdits, setGradingEdits] = useState({});
  const [unsaved, setUnsaved] = useState({});
  const [savingGrades, setSavingGrades] = useState(false);

  // ------------------------------------------------------------------
  // Grading helpers (from TeacherTestReport.js)
  // ------------------------------------------------------------------
  const getEditsFor = (attemptId) => gradingEdits[attemptId] || {};
  const markDirty = useCallback((attemptId, dirty = true) =>
    setUnsaved((prev) => ({ ...prev, [attemptId]: dirty })), []);

  const updateGrading = useCallback((qid, data) => {
    setCurrentAttemptId((curId) => {
      if (!curId) return curId;
      setGradingEdits((prev) => {
        const perAttempt = prev[curId] || {};
        const prevQ = perAttempt[qid] || {};
        return {
          ...prev,
          [curId]: { ...perAttempt, [qid]: { ...prevQ, ...data } },
        };
      });
      setUnsaved((prev) => ({ ...prev, [curId]: true }));
      return curId;
    });
  }, []);

  const confirmDiscard = useCallback(async (attemptId) => {
    if (!unsaved[attemptId]) return true;
    if (Platform.OS === "web") {
      return window.confirm(t("You have unsaved grading changes. Discard them?"));
    }
    return new Promise((resolve) => {
      Alert.alert(
        t("Unsaved changes"),
        t("You have unsaved grading changes. Discard them?"),
        [
          { text: t("Cancel"), style: "cancel", onPress: () => resolve(false) },
          { text: t("Discard"), style: "destructive", onPress: () => resolve(true) },
        ]
      );
    });
  }, [unsaved, t]);

  // ------------------------------------------------------------------
  // Fetch test list
  // ------------------------------------------------------------------
  useEffect(() => {
    const loadTests = async () => {
      setLoadingTests(true);
      try {
        const res = await axios.get(`${FASTAPI_BASE_URL}/tests/manage`, {
          headers: auth?.token ? { Authorization: `Bearer ${auth.token}` } : {},
        });
        const data = res.data;
        const all = [...(data.published || []), ...(data.unpublished || [])];
        all.sort((a, b) => b.id - a.id);
        setTests(all);
        // auto-select first if none given
        if (!selectedTestId && all.length > 0) {
          setSelectedTestId(all[0].id);
        }
      } catch (err) {
        console.error("Error fetching tests:", err);
      } finally {
        setLoadingTests(false);
      }
    };
    loadTests();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ------------------------------------------------------------------
  // Fetch results for selected test
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!selectedTestId) { setResults([]); return; }
    const loadResults = async () => {
      setLoadingResults(true);
      setResults([]);
      setSelectedStudentEmail(null);
      setSelectedAttemptId(null);
      setCurrentAttemptId(null);
      setGradingEdits({});
      setUnsaved({});
      try {
        const res = await axios.get(`${FASTAPI_BASE_URL}/tests/${selectedTestId}/results`);
        setResults(res.data || []);
      } catch (err) {
        console.error("Error fetching results:", err);
      } finally {
        setLoadingResults(false);
      }
    };
    loadResults();
  }, [selectedTestId]);

  // ------------------------------------------------------------------
  // Group results by student (latest attempt first)
  // ------------------------------------------------------------------
  const grouped = useMemo(() => {
    const byStudent = new Map();
    for (const r of results) {
      const email = r.student_email || "unknown_student";
      const name = r.student_name || email;
      if (!byStudent.has(email)) byStudent.set(email, { name, attempts: [] });
      byStudent.get(email).attempts.push(r);
    }
    const arr = Array.from(byStudent.entries()).map(([email, { name, attempts }]) => {
      attempts.sort((a, b) => {
        const ta = a.completed_at ? new Date(a.completed_at).getTime() : 0;
        const tb = b.completed_at ? new Date(b.completed_at).getTime() : 0;
        return tb - ta;
      });
      attempts.forEach((a, idx) => (a.__attemptNo = idx + 1));
      return { email, name, attempts };
    });
    arr.sort((a, b) => a.name.localeCompare(b.name));
    return arr;
  }, [results]);

  // Auto-select first student
  useEffect(() => {
    if (!selectedStudentEmail && grouped.length > 0) {
      const first = grouped[0];
      setSelectedStudentEmail(first.email);
      if (first.attempts.length > 0) {
        setSelectedAttemptId(first.attempts[0].id);
        setCurrentAttemptId(first.attempts[0].id);
      }
    }
  }, [grouped, selectedStudentEmail]);

  const selectedStudentData = useMemo(
    () => grouped.find((g) => g.email === selectedStudentEmail) || null,
    [grouped, selectedStudentEmail]
  );
  const selectedAttempt = useMemo(
    () => results.find((r) => r.id === selectedAttemptId) || null,
    [results, selectedAttemptId]
  );

  // ------------------------------------------------------------------
  // Select a student (with unsaved check)
  // ------------------------------------------------------------------
  const handleSelectStudent = useCallback(async (student) => {
    if (currentAttemptId) {
      const ok = await confirmDiscard(currentAttemptId);
      if (!ok) return;
    }
    setSelectedStudentEmail(student.email);
    const first = student.attempts[0];
    if (first) {
      setSelectedAttemptId(first.id);
      setCurrentAttemptId(first.id);
    }
  }, [currentAttemptId, confirmDiscard]);

  const handleSelectAttempt = useCallback(async (attempt) => {
    if (currentAttemptId && currentAttemptId !== attempt.id) {
      const ok = await confirmDiscard(currentAttemptId);
      if (!ok) return;
    }
    setSelectedAttemptId(attempt.id);
    setCurrentAttemptId(attempt.id);
  }, [currentAttemptId, confirmDiscard]);

  // ------------------------------------------------------------------
  // Computed stats
  // ------------------------------------------------------------------
  const stats = useMemo(() => {
    const totalStudents = grouped.length;
    const completed = grouped.filter((g) => g.attempts.length > 0).length;
    const percentages = grouped
      .filter((g) => g.attempts.length > 0)
      .map((g) => {
        const latest = g.attempts[0];
        return latest.total_score ? Math.round((latest.score / latest.total_score) * 100) : 0;
      });
    const avg = percentages.length > 0
      ? Math.round(percentages.reduce((a, b) => a + b, 0) / percentages.length)
      : 0;
    const passed = percentages.filter((p) => p >= 50).length;
    return { totalStudents, completed, avg, passed };
  }, [grouped]);

  // ------------------------------------------------------------------
  // Grade distribution buckets
  // ------------------------------------------------------------------
  const distribution = useMemo(() => {
    const counts = BUCKETS.map(() => 0);
    for (const g of grouped) {
      if (g.attempts.length === 0) continue;
      const latest = g.attempts[0];
      const pct = latest.total_score ? Math.round((latest.score / latest.total_score) * 100) : 0;
      for (let i = 0; i < BUCKETS.length; i++) {
        if (pct >= BUCKETS[i].min && pct <= BUCKETS[i].max) { counts[i]++; break; }
      }
    }
    const maxCount = Math.max(...counts, 1);
    return BUCKETS.map((b, i) => ({
      ...b,
      count: counts[i],
      heightPct: Math.max((counts[i] / maxCount) * 100, 4),
    }));
  }, [grouped]);

  // ------------------------------------------------------------------
  // Save grades
  // ------------------------------------------------------------------
  const handleSaveGrades = useCallback(async () => {
    if (!selectedAttempt) return;
    setSavingGrades(true);
    const attemptId = selectedAttempt.id;
    const edits = getEditsFor(attemptId);

    // BDD 08/25/2026 - only submit answers that actually have a grade.
    // Do NOT turn unanswered manual-grading items into zero.
    const graded_answers =
      (selectedAttempt.answers || []).flatMap((entry) => {
        const qid = entry.test_question_id;
        const maxScore = getMaxScore(entry);
        const { suggested } = autoSuggestScore(entry);

        const overrideScoreRaw =
          edits[qid]?.teacher_score;

        const overrideScore =
          overrideScoreRaw !== undefined &&
          overrideScoreRaw !== null &&
          overrideScoreRaw !== ""
            ? Number(overrideScoreRaw)
            : undefined;

        const overrideComment =
          edits[qid]?.teacher_comment;

        const existingScore =
          entry.teacher_score !== null &&
          entry.teacher_score !== undefined
            ? Number(entry.teacher_score)
            : undefined;

        // IMPORTANT:
        // If there is no teacher edit, no existing grade,
        // and no auto-grade suggestion, this answer is still pending.
        if (
          overrideScore === undefined &&
          existingScore === undefined &&
          suggested === undefined
        ) {
          return [];
        }

        let finalScore =
          overrideScore !== undefined
            ? overrideScore
            : existingScore !== undefined
              ? existingScore
              : suggested;

        if (!Number.isFinite(finalScore)) {
          return [];
        }

        finalScore = Math.max(
          0,
          Math.min(maxScore, finalScore)
        );

        const finalComment =
          overrideComment ??
          entry.teacher_comment ??
          "";

        return [{
          test_question_id: parseInt(qid, 10),
          teacher_score: finalScore,
          teacher_comment: finalComment,
        }];
      });

    const payload = {
      student_test_id: attemptId,
      teacher_email: user_id,
      graded_answers,
    };

    try {
      await axios.post(`${FASTAPI_BASE_URL}/teacher/grade-student-test`, payload);
      if (Platform.OS === "web") {
        window.alert(t("Grades saved successfully."));
      } else {
        Alert.alert(t("Success"), t("Grades saved."));
      }
      markDirty(attemptId, false);
    } catch (err) {
      console.error("Grading error:", err);
      if (Platform.OS === "web") {
        window.alert(t("Failed to save grades."));
      } else {
        Alert.alert(t("Error"), t("Failed to save grades."));
      }
    } finally {
      setSavingGrades(false);
    }
  }, [selectedAttempt, gradingEdits, user_id, markDirty, t]);

  const handleDiscardChanges = useCallback(() => {
    if (!currentAttemptId) return;
    setGradingEdits((prev) => ({ ...prev, [currentAttemptId]: {} }));
    markDirty(currentAttemptId, false);
  }, [currentAttemptId, markDirty]);

  // ------------------------------------------------------------------
  // Render: Test selector dropdown
  // ------------------------------------------------------------------
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");

  const filteredTests = useMemo(() => {
    if (!dropdownSearch.trim()) return tests;
    const q = dropdownSearch.toLowerCase();
    return tests.filter((t) => (t.title || `Test #${t.id}`).toLowerCase().includes(q));
  }, [tests, dropdownSearch]);

  const selectedTestTitle = useMemo(
    () => tests.find((t) => t.id === selectedTestId)?.title || "Select a test...",
    [tests, selectedTestId]
  );

  const renderTestSelector = () => (
    <View style={styles.selectorRow}>
      <View style={[styles.selectWrapper, { backgroundColor: colors.cardBg, borderColor: colors.border, overflow: "visible" }]}>
        <TouchableOpacity
          onPress={() => { setDropdownOpen((v) => !v); setDropdownSearch(""); }}
          activeOpacity={0.7}
          style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", padding: 12 }}
          title={t("Select a test")}
        >
          <Text style={{ color: colors.textPrimary, fontSize: 14, fontWeight: "600", flex: 1 }} numberOfLines={1}>
            {selectedTestTitle}
          </Text>
          <MaterialCommunityIcons name={dropdownOpen ? "chevron-up" : "chevron-down"} size={20} color={colors.textSecondary} />
        </TouchableOpacity>

        {dropdownOpen && (
          <View style={[styles.dropdownList, { backgroundColor: colors.cardBg, borderColor: colors.border }]}>
            <TextInput
              style={[styles.dropdownSearchInput, { backgroundColor: colors.inputBg, color: colors.textPrimary, borderColor: colors.border }]}
              placeholder={t("Select a test...")}
              placeholderTextColor={colors.textMuted}
              value={dropdownSearch}
              onChangeText={setDropdownSearch}
              autoFocus
            />
            <ScrollView style={{ maxHeight: 200, backgroundColor: colors.cardBg }} nestedScrollEnabled>
              {filteredTests.map((t) => {
                const isSelected = t.id === selectedTestId;
                return (
                  <TouchableOpacity
                    key={t.id}
                    onPress={() => { setSelectedTestId(t.id); setDropdownOpen(false); }}
                    style={[
                      styles.dropdownItem,
                      { backgroundColor: isSelected ? colors.accent : colors.cardBg },
                    ]}
                    activeOpacity={0.7}
                  >
                    <Text style={{ color: isSelected ? "#ffffff" : colors.textPrimary, fontSize: 14 }} numberOfLines={1}>
                      {t.title || `Test #${t.id}`}
                    </Text>
                  </TouchableOpacity>
                );
              })}
              {filteredTests.length === 0 && (
                <Text style={{ color: colors.textMuted, padding: 12, fontSize: 13, textAlign: "center" }}>{t("No tests found")}</Text>
              )}
            </ScrollView>
          </View>
        )}
      </View>
    </View>
  );

  // ------------------------------------------------------------------
  // Render: Grade distribution chart
  // ------------------------------------------------------------------
  const renderDistribution = () => (
    <View style={[styles.chartCard, { backgroundColor: colors.cardBg, borderColor: colors.border }]}>
      <Text style={[styles.chartTitle, { color: colors.textMuted }]}>{t("Grade Distribution")}</Text>
      <View style={styles.chartBars}>
        {distribution.map((b, i) => (
          <View key={b.label} style={styles.barCol}>
            <View style={styles.barTrack}>
              <View
                style={[
                  styles.bar,
                  {
                    height: `${b.heightPct}%`,
                    backgroundColor: bucketColor(i, isDarkMode),
                    opacity: BUCKET_OPACITY[i],
                  },
                ]}
              />
            </View>
            <Text style={[styles.barCount, { color: colors.textSecondary }]}>{b.count}</Text>
            <Text style={[styles.barLabel, { color: colors.textMuted }]}>{b.label}</Text>
          </View>
        ))}
      </View>
    </View>
  );

  // ------------------------------------------------------------------
  // Render: Student list item
  // ------------------------------------------------------------------
  const renderStudentItem = (student) => {
    const isActive = student.email === selectedStudentEmail;
    const latest = student.attempts[0];
    const pct = latest?.total_score
      ? Math.round((latest.score / latest.total_score) * 100) : 0;
    const badge = scoreBadgeColors(pct, colors);

    return (
      <TouchableOpacity
        key={student.email}
        onPress={() => handleSelectStudent(student)}
        activeOpacity={0.7}
        style={[
          styles.studentItem,
          { borderBottomColor: colors.border },
          isActive && {
            backgroundColor: colors.accentLight,
            borderLeftWidth: 3,
            borderLeftColor: colors.accent,
          },
        ]}
      >
        <View style={[styles.avatar, { backgroundColor: avatarColor(student.name) }]}>
          <Text style={styles.avatarText}>{initials(student.name)}</Text>
        </View>
        <View style={styles.studentInfo}>
          <Text style={[styles.studentName, { color: colors.textPrimary }]} numberOfLines={1}>
            {student.name}
          </Text>
          <Text style={[styles.studentDate, { color: colors.textMuted }]}>
            {fmtDate(latest?.completed_at)}
          </Text>
        </View>
        {/* PDF icon indicators */}
        {(latest?.pdf_path || latest?.graded_pdf_path) && (
          <View style={styles.studentPdfIcons}>
            {latest.pdf_path && (
              <TouchableOpacity
                onPress={(e) => { e.stopPropagation(); openPDF(latest.pdf_path); }}
                activeOpacity={0.7}
                title={t("View submitted PDF")}
                hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
              >
                <MaterialCommunityIcons
                  name="file-pdf-box"
                  size={18}
                  color={colors.textMuted}
                />
              </TouchableOpacity>
            )}
            {latest.graded_pdf_path && (
              <TouchableOpacity
                onPress={(e) => { e.stopPropagation(); openPDF(latest.graded_pdf_path); }}
                activeOpacity={0.7}
                title={t("View graded PDF")}
                hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
              >
                <MaterialCommunityIcons
                  name="file-check-outline"
                  size={18}
                  color={isDarkMode ? "#34d399" : "#10b981"}
                />
              </TouchableOpacity>
            )}
          </View>
        )}
        <View style={[styles.scoreBadge, { backgroundColor: badge.bg }]}>
          <Text style={[styles.scoreBadgeText, { color: badge.text }]}>{pct}%</Text>
        </View>
      </TouchableOpacity>
    );
  };

  // ------------------------------------------------------------------
  // Render: Grading panel (right side)
  // ------------------------------------------------------------------
  const renderGradingPanel = () => {
    if (!selectedAttempt) {
      return (
        <View style={styles.emptyPanel}>
          <MaterialCommunityIcons name="clipboard-text-outline" size={48} color={colors.textMuted} />
          <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
            {t("Select a student to view and grade their submission")}
          </Text>
        </View>
      );
    }

    const attempt = selectedAttempt;
    const editsForThis = getEditsFor(currentAttemptId);
    const totalPct = attempt.total_score
      ? Math.round((attempt.score / attempt.total_score) * 100) : 0;

    return (
      <ScrollView contentContainerStyle={styles.gradingContent}>
        {/* Student header */}
        <View style={{ marginBottom: 20 }}>
          <Text style={[styles.gradingName, { color: colors.textPrimary }]}>
            {selectedStudentData?.name || attempt.student_email}
          </Text>
          <Text style={[styles.gradingSub, { color: colors.textSecondary }]}>
            Submitted {fmtDate(attempt.completed_at)} {" "}  Score:{" "}
            <Text style={{ fontWeight: "700" }}>
              {attempt.score} / {attempt.total_score} ({totalPct}%)
            </Text>
          </Text>

          {/* PDF download buttons */}
          {(attempt.pdf_path || attempt.graded_pdf_path) && (
            <View style={styles.pdfRow}>
              {attempt.pdf_path && (
                <TouchableOpacity
                  onPress={() => openPDF(attempt.pdf_path)}
                  style={[
                    styles.pdfChip,
                    {
                      backgroundColor: isDarkMode
                        ? "rgba(148,163,184,0.12)"
                        : "rgba(100,116,139,0.08)",
                    },
                  ]}
                  activeOpacity={0.7}
                  title={t("View submitted PDF")}
                >
                  <MaterialCommunityIcons
                    name="file-pdf-box"
                    size={16}
                    color={colors.textSecondary}
                  />
                  <Text style={[styles.pdfChipText, { color: colors.textSecondary }]}>
                    {t("View Submitted PDF")}
                  </Text>
                </TouchableOpacity>
              )}
              {attempt.graded_pdf_path && (
                <TouchableOpacity
                  onPress={() => openPDF(attempt.graded_pdf_path)}
                  style={[
                    styles.pdfChip,
                    {
                      backgroundColor: isDarkMode
                        ? "rgba(52,211,153,0.12)"
                        : "rgba(16,185,129,0.08)",
                    },
                  ]}
                  activeOpacity={0.7}
                  title={t("View graded PDF")}
                >
                  <MaterialCommunityIcons
                    name="file-check-outline"
                    size={16}
                    color={isDarkMode ? "#34d399" : "#10b981"}
                  />
                  <Text style={[styles.pdfChipText, { color: isDarkMode ? "#34d399" : "#10b981" }]}>
                    {t("View Graded PDF")}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* Attempt selector if multiple */}
          {selectedStudentData && selectedStudentData.attempts.length > 1 && (
            <View style={styles.attemptRow}>
              {selectedStudentData.attempts.map((a) => {
                const isSel = a.id === selectedAttemptId;
                return (
                  <TouchableOpacity
                    key={a.id}
                    onPress={() => handleSelectAttempt(a)}
                    style={[
                      styles.attemptChip,
                      {
                        backgroundColor: isSel ? colors.accent : colors.cardBg,
                        borderColor: isSel ? colors.accent : colors.border,
                      },
                    ]}
                  >
                    <Text style={{ color: isSel ? "#ffffff" : colors.textSecondary, fontSize: 12, fontWeight: "600" }}>
                      Attempt #{a.__attemptNo}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}
        </View>

        {/* Questions */}
        {(attempt.answers || []).map((entry, idx) => {
          const qid = entry.test_question_id;
          const totalQ = attempt.answers?.length || 0;
          const parsedQ = parseJSONSafe(entry.question_text, {});
          const questionText = parsedQ?.text || t("[Question text missing]");
          const { studentDisplay, correctDisplay } = buildAnswerDisplay(entry, t);
          const maxScoreNum = getMaxScore(entry);
          const { suggested, isAutoGradable } = autoSuggestScore(entry);

          const edit = editsForThis[qid] || {};
          const displayScore =
            edit.teacher_score !== undefined && edit.teacher_score !== null
              ? String(edit.teacher_score)
              : entry.teacher_score !== null && entry.teacher_score !== undefined
              ? String(entry.teacher_score)
              : suggested !== undefined
              ? String(suggested)
              : "";
          const displayComment = edit.teacher_comment ?? entry.teacher_comment ?? "";

          // Determine if student answer is correct (for coloring)
          const isCorrect = isAutoGradable && suggested !== undefined && suggested === maxScoreNum;
          const isIncorrect = isAutoGradable && suggested !== undefined && suggested === 0;

          return (
            <View key={qid} style={[styles.questionCard, { borderColor: colors.border }]}>
              {/* Question header */}
              <View style={styles.qHeader}>
                <Text style={[styles.qLabel, { color: colors.textMuted }]}>
                  {t("Question {current} of {total}", { current: idx + 1, total: totalQ })}
                </Text>
                <Text style={[styles.qType, { color: colors.accent }]}>
                  {qtypeLabel(entry.question_type, t)} {" "} {maxScoreNum} {t("pts")}
                </Text>
              </View>

              {/* Question text */}
              <Text style={[styles.qText, { color: colors.textPrimary }]}>
                {questionText}
              </Text>

              {/* Answer area */}
              <View style={styles.answerRow}>
                <View
                  style={[
                    styles.answerBox,
                    {
                      backgroundColor: isCorrect
                        ? colors.greenBg
                        : isIncorrect
                        ? colors.redBg
                        : (isDarkMode ? "rgba(255,255,255,0.04)" : "#f8fafc"),
                      borderColor: isCorrect ? colors.green : isIncorrect ? colors.red : colors.border,
                    },
                  ]}
                >
                  <Text style={[styles.answerLabel, { color: colors.textMuted }]}>
                    {t("Student Answer")}{isCorrect ? " " + t("(Correct)") : isIncorrect ? " " + t("(Incorrect)") : ""}
                  </Text>
                  <Text style={{ color: colors.textPrimary, fontSize: 13 }}>{studentDisplay}</Text>
                </View>
                {isAutoGradable && (
                  <View style={[styles.answerBox, { backgroundColor: colors.greenBg, borderColor: colors.green }]}>
                    <Text style={[styles.answerLabel, { color: colors.textMuted }]}>{t("Correct Answer")}</Text>
                    <Text style={{ color: colors.greenText, fontSize: 13 }}>{correctDisplay}</Text>
                  </View>
                )}
              </View>

              {/* Scoring row */}
              <View style={styles.scoringRow}>
                <Text style={[styles.scoringLabel, { color: colors.textSecondary }]}>{t("Score:")}</Text>
                <TextInput
                  value={displayScore}
                  onChangeText={(text) => {
                    // BDD 08/25/2026 - allow decimal grading such as 0.25, 0.75, 1.5
                    if (/^\d*\.?\d*$/.test(text)) {
                      updateGrading(qid, {
                        teacher_score: text
                      });
                    }
                  }}
                  keyboardType="decimal-pad"
                  style={[
                    styles.scoreInput,
                    {
                      backgroundColor: colors.inputBg,
                      borderColor: colors.inputBorder,
                      color: colors.textPrimary,
                    },
                  ]}
                />
                <Text style={[styles.scoringMax, { color: colors.textMuted }]}>/ {maxScoreNum}</Text>
                <TextInput
                  value={displayComment}
                  onChangeText={(text) => updateGrading(qid, { teacher_comment: text })}
                  placeholder={t("Optional comment...")}
                  placeholderTextColor={colors.textMuted}
                  style={[
                    styles.commentInput,
                    {
                      backgroundColor: colors.inputBg,
                      borderColor: colors.inputBorder,
                      color: colors.textPrimary,
                    },
                  ]}
                />
              </View>
            </View>
          );
        })}

        {/* Action buttons */}
        <View style={[styles.actionBar, { borderTopColor: colors.border }]}>
          <TouchableOpacity
            onPress={handleDiscardChanges}
            style={[styles.btnSecondary, { borderColor: colors.border }]}
            activeOpacity={0.7}
            title={t("Discard Changes")}
          >
            <Text style={{ color: colors.textSecondary, fontSize: 14, fontWeight: "600" }}>
              {t("Discard Changes")}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={handleSaveGrades}
            disabled={savingGrades}
            style={[styles.btnPrimary, { backgroundColor: colors.accent, opacity: savingGrades ? 0.6 : 1 }]}
            activeOpacity={0.7}
            title={t("Save Changes")}
          >
            {savingGrades ? (
              <ActivityIndicator size="small" color="#ffffff" />
            ) : (
              <>
                <MaterialCommunityIcons name="check" size={16} color="#ffffff" />
                <Text style={{ color: "#ffffff", fontSize: 14, fontWeight: "600", marginLeft: 6 }}>
                  {t("Save Changes")}
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>
    );
  };

  // ------------------------------------------------------------------
  // Main render
  // ------------------------------------------------------------------
  if (loadingTests) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.accent} />
        <Text style={{ color: colors.textSecondary, marginTop: 12 }}>{t("Loading tests...")}</Text>
      </View>
    );
  }

  return (
    <View style={styles.wrapper}>
      {/* Test selector */}
      {renderTestSelector()}

      {/* Stats row */}
      <TestStatsRow
        isDarkMode={isDarkMode}
        stats={[
          { label: t("Students"), value: String(stats.totalStudents), color: "indigo", icon: <MaterialCommunityIcons name="account-group-outline" size={20} color="currentColor" /> },
          { label: t("Completed"), value: String(stats.completed), color: "teal", icon: <MaterialCommunityIcons name="check-circle-outline" size={20} color="currentColor" /> },
          { label: t("Avg. Score"), value: `${stats.avg}%`, color: "amber", icon: <MaterialCommunityIcons name="chart-line" size={20} color="currentColor" /> },
          { label: t("Passed"), value: String(stats.passed), color: "green", icon: <MaterialCommunityIcons name="trophy-outline" size={20} color="currentColor" /> },
        ]}
      />

      {/* Grade distribution */}
      {renderDistribution()}

      {/* Loading state */}
      {loadingResults ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.accent} />
          <Text style={{ color: colors.textSecondary, marginTop: 12 }}>{t("Loading results...")}</Text>
        </View>
      ) : results.length === 0 && selectedTestId ? (
        <View style={styles.emptyPanel}>
          <MaterialCommunityIcons name="clipboard-text-outline" size={48} color={colors.textMuted} />
          <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
            {t("No results available for this test yet.")}
          </Text>
        </View>
      ) : results.length > 0 ? (
        /* Split layout: student list + grading panel */
        <View
          style={[
            styles.splitLayout,
            {
              flexDirection: isMobile ? "column" : "row",
              borderColor: colors.border,
              backgroundColor: colors.cardBg,
            },
          ]}
        >
          {/* Left panel: student list */}
          <View
            style={[
              styles.leftPanel,
              {
                width: isMobile ? "100%" : 320,
                borderRightWidth: isMobile ? 0 : 1,
                borderRightColor: colors.border,
                borderBottomWidth: isMobile ? 1 : 0,
                borderBottomColor: colors.border,
              },
            ]}
          >
            <View style={[styles.studentListHeader, { borderBottomColor: colors.border }]}>
              <Text style={[styles.studentListTitle, { color: colors.textPrimary }]}>
                {t("Students")} ({stats.completed} {t("submitted")})
              </Text>
            </View>
            <ScrollView style={isMobile ? { maxHeight: 240 } : { flex: 1 }}>
              {grouped.map((student) => renderStudentItem(student))}
            </ScrollView>
          </View>

          {/* Right panel: grading */}
          <View style={styles.rightPanel}>
            {renderGradingPanel()}
          </View>
        </View>
      ) : null}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
import { StyleSheet } from "react-native";

const styles = StyleSheet.create({
  wrapper: { flex: 1 },
  loadingContainer: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 60,
  },

  /* Test selector */
  selectorRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 24,
    zIndex: 1000,
    position: "relative",
  },
  selectWrapper: {
    flex: 1,
    maxWidth: 480,
    borderRadius: 10,
    borderWidth: 1,
    zIndex: 1000,
  },
  dropdownList: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    borderWidth: 1,
    borderRadius: 10,
    marginTop: 4,
    zIndex: 1001,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
  },
  dropdownSearchInput: {
    padding: 10,
    fontSize: 14,
    borderBottomWidth: 1,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  dropdownItem: {
    paddingVertical: 10,
    paddingHorizontal: 14,
  },

  /* Chart */
  chartCard: {
    borderWidth: 1,
    borderRadius: 16,
    padding: 20,
    marginBottom: 24,
  },
  chartTitle: {
    fontSize: 13,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 20,
  },
  chartBars: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 6,
  },
  barCol: {
    flex: 1,
    alignItems: "center",
  },
  barTrack: {
    width: "100%",
    height: 80,
    justifyContent: "flex-end",
  },
  bar: {
    width: "100%",
    borderRadius: 4,
    minHeight: 3,
  },
  barCount: {
    fontSize: 11,
    fontWeight: "700",
    marginTop: 4,
  },
  barLabel: {
    fontSize: 10,
    marginTop: 2,
  },

  /* Split layout */
  splitLayout: {
    borderWidth: 1,
    borderRadius: 16,
    overflow: "hidden",
    minHeight: 500,
  },
  leftPanel: {
    // width set dynamically
  },
  rightPanel: {
    flex: 1,
  },

  /* Student list */
  studentListHeader: {
    padding: 16,
    borderBottomWidth: 1,
  },
  studentListTitle: {
    fontSize: 14,
    fontWeight: "700",
  },
  studentItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    paddingHorizontal: 16,
    gap: 12,
    borderBottomWidth: 1,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
  },
  avatarText: {
    color: "#ffffff",
    fontSize: 13,
    fontWeight: "700",
  },
  studentInfo: {
    flex: 1,
    minWidth: 0,
  },
  studentName: {
    fontSize: 14,
    fontWeight: "600",
  },
  studentDate: {
    fontSize: 11,
    marginTop: 2,
  },
  scoreBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  scoreBadgeText: {
    fontSize: 12,
    fontWeight: "700",
  },

  /* Empty / placeholder */
  emptyPanel: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 60,
    gap: 12,
  },
  emptyText: {
    fontSize: 14,
    textAlign: "center",
    maxWidth: 300,
  },

  /* Grading panel */
  gradingContent: {
    padding: 24,
  },
  gradingName: {
    fontSize: 18,
    fontWeight: "700",
    marginBottom: 4,
  },
  gradingSub: {
    fontSize: 13,
  },
  attemptRow: {
    flexDirection: "row",
    gap: 8,
    marginTop: 12,
  },
  attemptChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },

  /* Question card */
  questionCard: {
    borderBottomWidth: 1,
    paddingBottom: 20,
    marginBottom: 20,
  },
  qHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  qLabel: {
    fontSize: 13,
    fontWeight: "700",
  },
  qType: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  qText: {
    fontSize: 14,
    lineHeight: 21,
    marginBottom: 12,
  },

  /* Answer boxes */
  answerRow: {
    flexDirection: "row",
    gap: 16,
    marginBottom: 12,
  },
  answerBox: {
    flex: 1,
    padding: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  answerLabel: {
    fontSize: 11,
    fontWeight: "600",
    marginBottom: 4,
  },

  /* Scoring */
  scoringRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  scoringLabel: {
    fontSize: 12,
    fontWeight: "600",
  },
  scoreInput: {
    width: 60,
    borderWidth: 1,
    borderRadius: 8,
    padding: 8,
    fontSize: 14,
    fontWeight: "600",
    textAlign: "center",
  },
  scoringMax: {
    fontSize: 12,
  },
  commentInput: {
    flex: 1,
    minWidth: 160,
    borderWidth: 1,
    borderRadius: 8,
    padding: 8,
    fontSize: 13,
  },

  /* Action bar */
  actionBar: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
  },
  btnSecondary: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  btnPrimary: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
  },

  /* PDF buttons */
  pdfRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
  },
  pdfChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  pdfChipText: {
    fontSize: 13,
    fontWeight: "600",
  },
  studentPdfIcons: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginRight: 4,
  },
});
